import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  BookOpen, ArrowLeft, Calendar, Loader, CheckCircle, Circle
} from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { useLanguage } from "../hooks/useLanguage";

export default function LessonHistoryPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const AGE_GROUP_LABELS = {
    little: { name: t('little_light'), color: "#FFD166", bg: "#FFF9E0" },
    big: { name: t('bible_explorer'), color: "#118AB2", bg: "#E0F4FF" },
    teen: { name: t('faith_builder'), color: "#9B5DE5", bg: "#F0E6FF" }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setError(null);
    try {
      const { data: result } = await axios.get(`${API}/lessons/history/all`);
      setData(result);
    } catch (e) {
      console.error(e);
      setError(t('could_not_load'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <div className="text-center">
          <Loader size={48} className="mx-auto animate-spin mb-4" color="#118AB2" />
          <p className="font-bold text-[#073B4C]">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-8"
        style={{ background: "linear-gradient(135deg, #073B4C 0%, #118AB2 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate("/parent")}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-4"
            data-testid="back-to-parent-btn">
            <ArrowLeft size={16} /> {t('back_to_dashboard')}
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-2xl p-3">
              <Calendar size={32} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('lesson_history_title')}
              </h1>
              <p className="text-white/70 text-sm font-medium">
                {data?.total_completed || 0} {t('completed').toLowerCase()} · {(data?.total_lessons || 0) - (data?.total_completed || 0)} {t('in_progress').toLowerCase()}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-4">
        {error ? (
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <p className="text-[#073B4C] font-bold mb-2">{error}</p>
            <button onClick={loadHistory}
              className="mt-2 text-white font-bold px-5 py-2 rounded-xl text-sm"
              style={{ background: "#118AB2" }}
              data-testid="retry-btn">
              {t('retry')}
            </button>
          </div>
        ) : !data?.children?.length ? (
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <BookOpen size={48} className="mx-auto mb-3 opacity-40" color="#118AB2" />
            <p className="text-gray-500 font-medium">{t('no_lessons_yet')}</p>
            <button onClick={() => navigate("/parent")}
              className="mt-4 text-white font-bold px-5 py-2 rounded-xl text-sm"
              style={{ background: "#118AB2" }}
              data-testid="go-to-dashboard-btn">
              {t('go_to_dashboard')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {data.children.map((childData) => {
              const { child, lessons, completed } = childData;
              const ageInfo = AGE_GROUP_LABELS[child.age_group] || AGE_GROUP_LABELS.little;
              const completedLessons = lessons.filter(l => l.completed);
              const inProgressLessons = lessons.filter(l => !l.completed);
              
              return (
                <div key={child.id} className="bg-white rounded-3xl shadow-xl overflow-hidden"
                  data-testid={`child-history-${child.id}`}>
                  {/* Child Header */}
                  <div className="p-4 flex items-center gap-4 border-b"
                    style={{ background: "linear-gradient(90deg, #E0F4FF, #F0E6FF)" }}>
                    <AvatarDisplay avatar={child.avatar} size={48} />
                    <div className="flex-1">
                      <h2 className="text-lg font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                        {child.name}
                      </h2>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: ageInfo.bg, color: ageInfo.color }}>
                        {ageInfo.name} · {t('age')} {child.age}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-[#06D6A0]">{completed} {t('completed').toLowerCase()}</p>
                      <p className="text-xs text-gray-400">{lessons.length} total</p>
                    </div>
                  </div>
                  
                  {/* Lessons List */}
                  <div className="p-4">
                    {lessons.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-4">{t('no_lessons_yet')}</p>
                    ) : (
                      <div className="space-y-4">
                        {/* Completed Lessons */}
                        {completedLessons.length > 0 && (
                          <div>
                            <p className="text-xs font-bold text-[#06D6A0] mb-2 flex items-center gap-1">
                              <CheckCircle size={12} /> {t('completed').toUpperCase()} ({completedLessons.length})
                            </p>
                            <div className="space-y-2">
                              {completedLessons.map((lesson, idx) => (
                                <div key={lesson.id || idx} 
                                  className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border-l-4 border-[#06D6A0]"
                                  data-testid={`lesson-completed-${lesson.id}`}>
                                  <CheckCircle size={18} color="#06D6A0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-bold text-[#073B4C] text-sm truncate">
                                      {lesson.lesson_title || (language === 'es' ? "Lección Bíblica" : "Bible Lesson")}
                                    </p>
                                    <p className="text-xs text-gray-500">{lesson.verse_reference} · {lesson.date}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}}
                        
                        {/* In Progress Lessons */}
                        {inProgressLessons.length > 0 && (
                          <div>
                            <p className="text-xs font-bold text-[#F77F00] mb-2 flex items-center gap-1">
                              <Circle size={12} /> {t('not_completed').toUpperCase()} ({inProgressLessons.length})
                            </p>
                            <div className="space-y-2">
                              {inProgressLessons.map((lesson, idx) => (
                                <div key={lesson.id || idx} 
                                  className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border-l-4 border-[#FFD166]"
                                  data-testid={`lesson-pending-${lesson.id}`}>
                                  <Circle size={18} color="#F77F00" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-bold text-[#073B4C] text-sm truncate">
                                      {lesson.lesson_title || (language === 'es' ? "Lección Bíblica" : "Bible Lesson")}
                                    </p>
                                    <p className="text-xs text-gray-500">{lesson.verse_reference} · {lesson.date}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
